import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { BrandContext } from '../../contexts/brand-context';
import { EventTrack, FilePath } from '../../services/utilities';
import { FaLinkedin, FaInstagram, FaFacebook, FaTrophy, FaMedal, FaStar, FaShare, FaRecycle, FaRandom, FaListOl, FaTimes, FaExclamationTriangle, FaMoon, FaSun, FaChartBar, FaGamepad, FaBolt, FaChartLine, FaHandPointer, FaRocket, FaFire } from 'react-icons/fa';
import { IoWaterOutline } from 'react-icons/io5';
import { GiWaterDrop, GiWaveCrest } from 'react-icons/gi';
import { BsDropletHalf, BsDropletFill } from 'react-icons/bs';
import './hydration-hero.scss';
import apiPublic from '../../services/api-public';

const ADJECTIVES = [
    'Swift', 'Mighty', 'Aqua', 'Rapid', 'Turbo', 'Flash', 'Hydro', 'Ultra', 
    'Super', 'Mega', 'Epic', 'Legendary', 'Crystal', 'Pure', 'Fresh', 'Cool',
    'Azure', 'Blue', 'Wave', 'Splash', 'Flowing', 'Rising', 'Rushing', 'Surging'
];

const NOUNS = [
    'Dolphin', 'Wave', 'Stream', 'River', 'Ocean', 'Tsunami', 'Rapids', 'Cascade',
    'Fountain','Waterfall', 'Tide', 'Current', 'Hydrator', 'Narwhal', 'Manatee',
    'Bottle', 'Drizzle', 'Droplet', 'Splash', 'Whale', 'Otter', 'Seal', 'Shark'
];

const getAchievementForTime = (time) => {
    if (time < 1) {
        return { title: 'Quantum Quencher', icon: 'rocket', color: '#9333ea' };
    } else if (time < 2) {
        return { title: 'Sonic Surge', icon: 'fire', color: '#dc2626' };
    } else if (time < 3) {
        return { title: 'Bolt Bottler', icon: 'bolt', color: '#f59e0b' };
    } else if (time < 5) {
        return { title: 'Rapid Refresher', icon: 'wave', color: '#06b6d4' };
    } else if (time < 8) {
        return { title: 'Steady Sipper', icon: 'dropletfill', color: '#3b82f6' };
    } else {
        return { title: 'Determined Dripper', icon: 'waterdrop', color: '#000000' };
    }
};

const getAchievementsForBestTime = (bestTime, hasPlayedAnyGames = false) => {
    const achievements = [];
    
    if (bestTime < 1) {
        achievements.push('Quantum Quencher');
    }
    if (bestTime < 2) {
        achievements.push('Sonic Surge');
    }
    if (bestTime < 3) {
        achievements.push('Bolt Bottler');
    }
    if (bestTime < 5) {
        achievements.push('Rapid Refresher');
    }
    if (bestTime < 8) {
        achievements.push('Steady Sipper');
    }
    if (hasPlayedAnyGames) {
        achievements.push('Determined Dripper');
    }
    
    return [...new Set(achievements)];
};

const isValidGeneratedName = (username) => {
    if (!username || typeof username !== 'string') return false;
    
    const adjectivePattern = ADJECTIVES.join('|');
    const nounPattern = NOUNS.join('|');
    const pattern = new RegExp(`^(${adjectivePattern})(${nounPattern})([1-9][0-9]{0,2})$`, 'i');
    
    const match = username.match(pattern);
    if (!match) return false;
    
    const number = parseInt(match[3], 10);
    
    if (number === 666 || number === 420 || number === 69) return false;
    if (number < 1 || number > 999) return false;
    
    return true;
};

const generateRandomName = async () => {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    let number = Math.floor(Math.random() * 999) + 1;
    while (number === 666 || number === 420 || number === 69) { 
        number = Math.floor(Math.random() * 999) + 1;
    }
    const name = `${adj}${noun}${number}`;

    const { data: exists } = await apiPublic.get(`/hydration-hero-name-check?name=${name}`);
    if (!exists) {
        return name;
    }
    return generateRandomName();
};

const HydrationHero = () => {
    const { selectedBrand } = useContext(BrandContext);
    const [ripples, setRipples] = useState([]);

    const [gameActive, setGameActive] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [waterLevel, setWaterLevel] = useState(0);
    const [tapCount, setTapCount] = useState(0);
    const [gameTime, setGameTime] = useState(0);
    const [gameComplete, setGameComplete] = useState(false);
    const [canDismissResults, setCanDismissResults] = useState(false);
    const [bestScore, setBestScore] = useState(() => {
        const saved = localStorage.getItem('hydrationHeroBestScore');
        return saved ? parseFloat(saved) : null;
    });
    const [achievement, setAchievement] = useState(null);
    
    const [playerName, setPlayerName] = useState(() => {
        return localStorage.getItem('hydrationHeroPlayerName') || null;
    });
    const [showNameModal, setShowNameModal] = useState(false);
    const [generatedNames, setGeneratedNames] = useState([]);
    const [isLoadingNames, setIsLoadingNames] = useState(false);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [isLogoLoaded, setIsLogoLoaded] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [playerStats, setPlayerStats] = useState(null);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    
    const [bottleShake, setBottleShake] = useState(false);
    const [showMilestone, setShowMilestone] = useState(false);
    const [milestoneText, setMilestoneText] = useState('');
    const [milestoneIcon, setMilestoneIcon] = useState(null);
    const [splashParticles, setSplashParticles] = useState([]);
    
    const gameTimerRef = useRef(null);
    const drainTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const startTimeRef = useRef(null);
    const lastMilestone = useRef(0);
    const bottleRef = useRef(null);
    const waterLevelRef = useRef(0);
    const tapCountRef = useRef(0);
    const animationFrameRef = useRef(null);
    const pendingAnimationsRef = useRef([]);

    const DRAIN_RATE = 0.5; // Water drains 0.5% every 100ms (5% per second)
    const TAP_FILL_AMOUNT = 2; // Each tap fills 2%

    const socialLinks = {
        linkedin: "https://www.linkedin.com/company/titleone-corporation",
        instagram: "https://www.instagram.com/titleoneco",
        facebook: "https://www.facebook.com/titleone"
    };

    const batchAnimations = useCallback(() => {
        if (animationFrameRef.current) return;
        
        animationFrameRef.current = requestAnimationFrame(() => {
            const animations = pendingAnimationsRef.current;
            pendingAnimationsRef.current = [];
            animationFrameRef.current = null;
            
            if (animations.length === 0) return;
            
            const rippleAnims = animations.filter(a => a.type === 'ripple');
            if (rippleAnims.length > 0) {
                setRipples(prev => {
                    const newRipples = rippleAnims.map(a => ({
                        x: a.x,
                        y: a.y,
                        id: a.id
                    }));
                    return [...prev, ...newRipples];
                });
                
                rippleAnims.forEach(a => {
                    setTimeout(() => {
                        setRipples(prev => prev.filter(r => r.id !== a.id));
                    }, 1000);
                });
            }
            
            const splashAnims = animations.filter(a => a.type === 'splash');
            if (splashAnims.length > 0) {
                setSplashParticles(prev => {
                    const newParticles = splashAnims.flatMap(a => a.particles);
                    return [...prev, ...newParticles];
                });
                
                splashAnims.forEach(a => {
                    setTimeout(() => {
                        setSplashParticles(prev => 
                            prev.filter(p => !a.particles.find(np => np.id === p.id))
                        );
                    }, 600);
                });
            }
            
            const shakeAnims = animations.filter(a => a.type === 'shake');
            if (shakeAnims.length > 0) {
                setBottleShake(true);
                setTimeout(() => setBottleShake(false), 300);
            }
        });
    }, []);

    const triggerRippleAnimation = useCallback((e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        pendingAnimationsRef.current.push({
            type: 'ripple',
            x: clientX,
            y: clientY,
            id: Date.now() + Math.random()
        });
        
        batchAnimations();
    }, [batchAnimations]);
    
    const triggerSplashAnimation = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        const particles = Array.from({ length: 8 }, (_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const distance = 30 + Math.random() * 20;
            return {
                id: Date.now() + Math.random() + i,
                x,
                y,
                tx: Math.cos(angle) * distance,
                ty: Math.sin(angle) * distance
            };
        });
        
        pendingAnimationsRef.current.push({
            type: 'splash',
            particles
        });
        
        batchAnimations();
    }, [batchAnimations]);

    const triggerBottleShakeAnimation = useCallback(() => {
        pendingAnimationsRef.current.push({ type: 'shake' });
        batchAnimations();
    }, [batchAnimations]);

    const triggerMilestoneAnimation = useCallback((level) => {
        const milestones = [25, 50, 75];
        const currentMilestone = milestones.find(m => level >= m && lastMilestone.current < m);
        
        if (currentMilestone) {
            lastMilestone.current = currentMilestone;
            const milestoneData = {
                25: { text: "Great Start!", icon: <BsDropletHalf /> },
                50: { text: "Halfway There!", icon: <GiWaveCrest /> },
                75: { text: "Almost Full!", icon: <BsDropletFill /> }
            };
            setMilestoneText(milestoneData[currentMilestone].text);
            setMilestoneIcon(milestoneData[currentMilestone].icon);
            setShowMilestone(true);
            setTimeout(() => setShowMilestone(false), 1000);
        }
    }, []);

    const triggerAllTapAnimations = useCallback((e) => {
        triggerRippleAnimation(e);
        triggerSplashAnimation(e);
        triggerBottleShakeAnimation();
    }, [triggerRippleAnimation, triggerSplashAnimation, triggerBottleShakeAnimation]);

    useEffect(() => {
        const getLeaderboard = async () => {
            const { data } = await apiPublic.get('/hydration-hero-leaderboard');
            setLeaderboard(data);
        };
        getLeaderboard();
    }, []);

    const generateNameOptions = async () => {
        setIsLoadingNames(true);
        const names = new Set();
        while (names.size < 3) {
            const name = await generateRandomName();
            names.add(name);
        }
        setGeneratedNames(Array.from(names));
        setIsLoadingNames(false);
    };

    const handleNameSelect = (name) => {
        setPlayerName(name);
        localStorage.setItem('hydrationHeroPlayerName', name);
        setShowNameModal(false);
    };

    const getPlayerStats = async (username) => {
        try {
            const { data } = await apiPublic.get(`/hydration-hero-player-stats?username=${username}`);
            return data;
        } catch (error) {
            console.error('Failed to fetch player stats:', error);
            return null;
        }
    };

    useEffect(() => {
        const loadPlayerStats = async () => {
            if (showStatsModal && playerName) {
                setIsLoadingStats(true);
                const stats = await getPlayerStats(playerName);
                
                if (stats && stats.bestTime !== null) {
                    stats.achievements = getAchievementsForBestTime(stats.bestTime, stats.gamesPlayed > 0);
                }
                
                setPlayerStats(stats);
                setIsLoadingStats(false);
            }
        };
        
        loadPlayerStats();
    }, [showStatsModal, playerName]);

    const handleChangeName = () => {
        setShowNameModal(true);
        generateNameOptions();
    };

    useEffect(() => {
        EventTrack('Hydration Hero', 'Page View', 'QR Code Scan');
        document.title = `Hydration Hero - ${selectedBrand.brandName}`;
        
        const storedName = localStorage.getItem('hydrationHeroPlayerName');
        if (storedName && !isValidGeneratedName(storedName)) {
            localStorage.removeItem('hydrationHeroPlayerName');
            setPlayerName(null);
            console.warn('Invalid username format detected in localStorage. Name has been cleared.');
        }
    },[selectedBrand.brandName]);

    const handleSocialClick = (platform, shareScore = false) => {
        const eventLabel = shareScore ? `Share Score - ${platform}` : platform;
        EventTrack('Hydration Hero', 'Social Media Click', eventLabel);
        window.open(socialLinks[platform], '_blank', 'noopener,noreferrer');
    };

    const handleTap = (e) => {
        triggerRippleAnimation(e);
    };
    
    const startGame = () => {
        if (!playerName) {
            setShowNameModal(true);
            generateNameOptions();
            return;
        }
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCountdown(3);
        
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev === 1) {
                    clearInterval(countdownTimerRef.current);
                    beginGame();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        
        EventTrack('Hydration Hero', 'Game Started', 'Hydration Hero');
    };
    
    const [botDetected, setBotDetected] = useState(false);
    const tapTimestamps = useRef([]);


    const beginGame = () => {
        setGameActive(true);
        setWaterLevel(0);
        setTapCount(0);
        waterLevelRef.current = 0;
        tapCountRef.current = 0;
        setGameTime(0);
        setGameComplete(false);
        setCanDismissResults(false);
        setAchievement(null);
        setBotDetected(false);
        lastMilestone.current = 0;
        startTimeRef.current = Date.now();
        
        tapTimestamps.current = [];
        
        document.body.classList.add('hydration-hero-active-game');
        
        gameTimerRef.current = setInterval(() => {
            if (startTimeRef.current) {
                setGameTime(((Date.now() - startTimeRef.current) / 1000).toFixed(3));
            }
        }, 10);
        
        drainTimerRef.current = setInterval(() => {
            setWaterLevel(prev => {
                const newLevel = Math.max(prev - DRAIN_RATE, 0);
                waterLevelRef.current = newLevel;
                return newLevel;
            });
        }, 100);
    };

    const handleBottleTap = useCallback((e) => {
        if (!gameActive || gameComplete || botDetected) return;

        e.preventDefault();
        e.stopPropagation();
        
        const now = Date.now();
        
        tapTimestamps.current.push(now);

        tapCountRef.current += 1;
        waterLevelRef.current = Math.min(waterLevelRef.current + TAP_FILL_AMOUNT, 100);
        
        const newLevel = waterLevelRef.current;
        const newCount = tapCountRef.current;
        
        requestAnimationFrame(() => {
            setTapCount(newCount);
            setWaterLevel(newLevel);
        });
        
        if (newLevel >= 25 && newLevel < 100) {
            triggerMilestoneAnimation(newLevel);
        }
        
        if (newLevel >= 100) {
            completeGame();
        }
        triggerAllTapAnimations(e);
    }, [gameActive, gameComplete, botDetected, triggerAllTapAnimations, triggerMilestoneAnimation]);

    useEffect(() => {
        const bottleElement = bottleRef.current;
        if (!bottleElement) return;

        const handleTouchStart = (e) => {
            handleBottleTap(e);
        };

        const handleTouchMove = (e) => {
            if (gameActive && !gameComplete) {
                e.preventDefault();
            }
        };

        if (gameActive && !gameComplete) {
            bottleElement.addEventListener('touchstart', handleTouchStart, { passive: false });
            bottleElement.addEventListener('touchmove', handleTouchMove, { passive: false });
            
            return () => {
                bottleElement.removeEventListener('touchstart', handleTouchStart);
                bottleElement.removeEventListener('touchmove', handleTouchMove);
            };
        }
    }, [gameActive, gameComplete, handleBottleTap]);

    const completeGame = () => {
        if (gameComplete) return;
        
        setGameComplete(true);
        clearInterval(gameTimerRef.current);
        clearInterval(drainTimerRef.current);
        
        document.body.classList.remove('hydration-hero-active-game');
        
        const finalTime = ((Date.now() - startTimeRef.current) / 1000).toFixed(3);
        const score = parseFloat(finalTime);
        const finalTaps = tapCountRef.current;
        
        setGameTime(score);
        
        setTimeout(() => {
            setCanDismissResults(true);
        }, 2000);
        
        const newAchievement = getAchievementForTime(score);
        
        setAchievement(newAchievement);
        
        const isNewBest = bestScore === null || score < bestScore;
        if (isNewBest) {
            setBestScore(score);
            localStorage.setItem('hydrationHeroBestScore', score.toString());
        }

        if (!isValidGeneratedName(playerName)) {
            console.error('Invalid username format detected. Score will not be submitted to leaderboard.');
            return;
        }
        
        const newEntry = {
            username: playerName,
            time: score,
            tapCount: finalTaps
        };

        const postLog = async (newEntry) => {
            try {
                await apiPublic.post('hydration-hero-log', newEntry);
            } catch (error) {
                console.error('Failed to submit score:', error);
            }
        };
        postLog(newEntry);

        const updatedLeaderboard = [...leaderboard, newEntry]
            .sort((a, b) => a.time - b.time)
            .slice(0, 10);
        
        setLeaderboard(updatedLeaderboard);
        
        const playerRank = updatedLeaderboard.findIndex(entry => 
            entry.username === playerName && entry.time === score
        );
        
        if (playerRank >= 0) {
            newAchievement.leaderboardRank = playerRank + 1;
            setAchievement(newAchievement);
        }
        
        EventTrack('Hydration Hero', 'Game Completed', `Time: ${score}s, Taps: ${finalTaps}`);
    };

    const resetGame = () => {
        setGameActive(false);
        setCountdown(0);
        setWaterLevel(0);
        setTapCount(0);
        setGameTime(0);
        setGameComplete(false);
        setAchievement(null);
        setBotDetected(false);
        
        tapTimestamps.current = [];
        
        document.body.classList.remove('hydration-hero-active-game');
        
        if (gameTimerRef.current) {
            clearInterval(gameTimerRef.current);
        }
        if (drainTimerRef.current) {
            clearInterval(drainTimerRef.current);
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
        }
    };

    useEffect(() => {
        return () => {
            document.body.classList.remove('hydration-hero-active-game');
            
            if (gameTimerRef.current) {
                clearInterval(gameTimerRef.current);
            }
            if (drainTimerRef.current) {
                clearInterval(drainTimerRef.current);
            }
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);
    
    const getBottleFillClass = () => {
        if (waterLevel < 33) return 'fill-low';
        if (waterLevel < 66) return 'fill-medium';
        return 'fill-high';
    };
    
    const getWaterFillClass = () => {
        if (waterLevel < 40) return 'fill-level-low';
        if (waterLevel > 70) return 'fill-level-high';
        return '';
    };
    
    const getPlayerRank = () => {
        if (!bestScore) return null;
        const rank = leaderboard.findIndex(entry => entry.username === playerName && entry.time === bestScore);
        return rank >= 0 ? rank + 1 : null;
    };
    
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('hydrationHeroDarkMode');
        return saved ? JSON.parse(saved) : false;
    });
    
    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        localStorage.setItem('hydrationHeroDarkMode', JSON.stringify(newMode));
    };
    
   
    useEffect(() => {
        if (isDarkMode) {
            document.body.classList.add('hydration-hero-dark-mode');
        } else {
            document.body.classList.remove('hydration-hero-dark-mode');
        }
        
        return () => {
            document.body.classList.remove('hydration-hero-dark-mode');
        };
    }, [isDarkMode]);

    return (
        <div id="hydrationHero" className={`bottle-page ${isDarkMode ? 'dark-mode' : ''}`} onClick={handleTap}>
            <button
                className="theme-toggle-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleDarkMode();
                }}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
                {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>
            
            {ripples.map(ripple => (
                <div
                    key={ripple.id}
                    className="ripple"
                    style={{
                        left: ripple.x,
                        top: ripple.y
                    }}
                />
            ))}

            {showNameModal && (
                <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="name-modal glass-card">
                        <h2 className="hh-modal-title">Choose Your Hero Name</h2>
                        <p className="modal-subtitle">Pick one of these randomly generated names:</p>
                        
                        {playerName && (
                            <div className="name-change-warning">
                                <FaExclamationTriangle style={{ color: '#ff9800' }} />
                                <p>
                                    <strong>Warning:</strong> Changing your name will cause you to lose track of your current stats. 
                                    Your stats are tied to your username and cannot be transferred.
                                </p>
                            </div>
                        )}
                        
                        <div className="name-options">
                            {isLoadingNames ? (
                                <>
                                    <div className="name-skeleton skeleton-pulse"></div>
                                    <div className="name-skeleton skeleton-pulse"></div>
                                    <div className="name-skeleton skeleton-pulse"></div>
                                </>
                            ) : (
                                generatedNames.map((name, index) => (
                                    <button
                                        key={index}
                                        className="name-option"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleNameSelect(name);
                                        }}
                                    >
                                        {name}
                                    </button>
                                ))
                            )}
                        </div>
                        
                        <button
                            className="btn btn-outline regenerate-btn mb-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                generateNameOptions();
                            }}
                            disabled={isLoadingNames}
                        >
                            <FaRandom /> Generate New Names
                        </button>
                        
                        {playerName && (
                            <button
                                className="btn btn-outline regenerate-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowNameModal(false);
                                }}
                            >
                                <FaTimes /> Keep Current Name
                            </button>
                        )}
                      </div>
                </div>
            )}

            {showLeaderboard && (
                <div className="modal-overlay" onClick={(e) => {
                    e.stopPropagation();
                    setShowLeaderboard(false);
                }}>
                    <div className="leaderboard-modal glass-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="hh-modal-title">
                                <FaTrophy style={{ color: '#b3985d' }} /> Top Hydrators
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowLeaderboard(false)}
                                aria-label="Close leaderboard"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="leaderboard-list">
                            {leaderboard.map((entry, index) => {
                                const isPlayerEntry = entry.username === playerName && leaderboard.filter(e => e.username === playerName).includes(entry);                                
                                return (
                                    <div
                                        key={index}
                                        className={`leaderboard-entry ${isPlayerEntry ? 'player-entry' : ''}`}
                                    >
                                        <div className="entry-rank">
                                            {index < 3 ? (
                                                <div className={`rank-medal-combo rank-${index + 1}`}>
                                                    <FaTrophy className="rank-trophy" />
                                                    <span className="rank-position">{index + 1}</span>
                                                </div>
                                            ) : (
                                                <span className="rank-number">#{index + 1}</span>
                                            )}
                                        </div>
                                        <div className="entry-content">
                                            <div className="entry-name">
                                                <span className="username-text">{entry.username}</span>
                                                {isPlayerEntry && <span className="you-badge">YOU</span>}
                                            </div>
                                            <div className="entry-stats">
                                                <span className="entry-time">{entry.time}s</span>
                                                <span className="entry-taps">{entry.tapCount} taps</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {getPlayerRank() && (
                            <div className="player-rank-badge">
                                Your Rank: #{getPlayerRank()} 🎯
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showStatsModal && playerName && (
                <div className="modal-overlay" onClick={(e) => {
                    e.stopPropagation();
                    setShowStatsModal(false);
                }}>
                    <div className="stats-modal glass-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="hh-modal-title">
                                <FaChartBar style={{ color: '#b3985d ' }} /> Your Stats
                            </h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowStatsModal(false)}
                                aria-label="Close stats"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="stats-player-info">
                            <div className="stats-player-name">{playerName}</div>
                        </div>
                        
                        {isLoadingStats ? (
                            <div className="stats-loading">
                                <div className="stats-skeleton-grid">
                                    <div className="info-skeleton skeleton-pulse"></div>
                                    <div className="info-skeleton skeleton-pulse"></div>
                                    <div className="info-skeleton skeleton-pulse"></div>
                                    <div className="info-skeleton skeleton-pulse"></div>
                                </div>
                            </div>
                        ) : playerStats ? (
                            <div className="stats-content">
                                <div className="stats-grid">
                                    <div className="info-card glass-card">
                                        <div className="info-icon"><FaGamepad style={{ color: '#b3985d ' }} /></div>
                                        <div className="info-number">{playerStats.gamesPlayed}</div>
                                        <div className="info-label">Games Played</div>
                                    </div>
                                    
                                    <div className="info-card glass-card">
                                        <div className="info-icon"><FaBolt style={{ color: '#b3985d ' }} /></div>
                                        <div className="info-number">
                                            {playerStats.bestTime !== null ? `${playerStats.bestTime}s` : '--'}
                                        </div>
                                        <div className="info-label">Best Time</div>
                                    </div>
                                    
                                    <div className="info-card glass-card">
                                        <div className="info-icon"><FaChartLine style={{ color: '#b3985d ' }} /></div>
                                        <div className="info-number">
                                            {playerStats.averageTime !== null ? `${playerStats.averageTime}s` : '--'}
                                        </div>
                                        <div className="info-label">Average Time</div>
                                    </div>
                                    
                                    <div className="info-card glass-card">
                                        <div className="info-icon"><FaHandPointer style={{ color: '#b3985d ' }} /></div>
                                        <div className="info-number">{playerStats.totalTaps ? playerStats.totalTaps.toLocaleString() : 0}</div>
                                        <div className="info-label">Total Taps</div>
                                    </div>
                                </div>
                                
                                {playerStats.achievements && playerStats.achievements.length > 0 && (
                                    <div className="achievements-section">
                                        <h3 className="achievements-title">
                                            <FaTrophy style={{ color: '#b3985d ' }} /> Achievements
                                        </h3>
                                        <div className="achievements-list">
                                            {playerStats.achievements.map((achievement, index) => (
                                                <div key={index} className="achievement-badge">
                                                    {achievement === 'Quantum Quencher' && <FaRocket style={{ color: '#9333ea' }} />}
                                                    {achievement === 'Sonic Surge' && <FaFire style={{ color: '#dc2626' }} />}
                                                    {achievement === 'Bolt Bottler' && <FaBolt style={{ color: '#f59e0b' }} />}
                                                    {achievement === 'Rapid Refresher' && <GiWaveCrest style={{ color: '#06b6d4' }} />}
                                                    {achievement === 'Steady Sipper' && <BsDropletFill style={{ color: '#3b82f6' }} />}
                                                    {achievement === 'Determined Dripper' && <GiWaterDrop style={{ color: '#000000' }} />}
                                                    <span>{achievement}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {playerStats.gamesPlayed === 0 && (
                                    <div className="no-stats-message">
                                        <GiWaterDrop style={{ fontSize: '3rem', color: '#b3985d ', opacity: 0.5 }} />
                                        <p>No games played yet. Start playing to track your stats!</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="no-stats-message">
                                <GiWaterDrop style={{ fontSize: '3rem', color: '#b3985d ', opacity: 0.5 }} />
                                <p>Unable to load stats. Please try again later.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="game-container">
                {countdown > 0 && (
                    <div className="countdown-overlay">
                        <div className="countdown-number">{countdown}</div>
                        <div className="countdown-text">Get Ready!</div>
                    </div>
                )}
                
                {!gameActive && !gameComplete && countdown === 0 && (
                    <div className="game-welcome">
                        <div className="logo-container">
                            {!isLogoLoaded && (
                                <div className="logo-skeleton skeleton-pulse"></div>
                            )}
                            <img 
                                src={FilePath('/images/hydration-hero.webp')} 
                                className={`hydration-hero-logo ${isLogoLoaded ? 'loaded' : ''}`}
                                alt="Hydration Hero Logo"
                                onLoad={() => setIsLogoLoaded(true)}
                                style={{ display: isLogoLoaded ? 'block' : 'none' }}
                            />
                        </div>
                        <p className="game-tagline">How fast can you fill the bottle?</p>
                        
                        <div className="glass-card welcome-card">
                            <div className="game-rules">
                                <GiWaterDrop className="rules-icon" />
                                <p>Tap rapidly to fill the bottle.</p>
                                <p>Fill to 100% as fast as you can.</p>
                                <p>Water constantly drains - stay quick!</p>
                            </div>
                            
                            <button 
                                className="btn btn-primary game-button-large"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    startGame();
                                }}
                            >
                                {playerName ? 'Start Game' : 'Choose Name & Start'}
                            </button>
                            
                            {bestScore && (
                                <div className="best-score-large">
                                    <FaTrophy className="trophy-icon" />
                                    <div className="best-score-info">
                                        <span className="best-score-label">Personal Best</span>
                                        <span className="best-score-time">{bestScore}s</span>
                                    </div>
                                </div>
                            )}
                            
                            {playerName && (
                                <div className="player-info">
                                    <span className="player-label">Playing as:</span>
                                    <span className="player-name">{playerName}</span>
                                    <button
                                        className="btn btn-sm btn-outline change-name-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChangeName();
                                        }}
                                    >
                                        Change Name
                                    </button>
                                </div>
                            )}
                            
                            <button 
                                className="btn btn-secondary game-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowLeaderboard(true);
                                }}
                            >
                                <FaListOl /> View Leaderboard
                            </button>
                            


                            {playerName && (
                                <button 
                                    className="btn btn-secondary game-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowStatsModal(true);
                                    }}
                                >
                                    <FaChartBar /> View My Stats
                                </button>
                            )}
                        </div>
                        
                        <div className="glass-card eco-message">
                            <div className="eco-header">
                                <div className="eco-icon-circle">
                                    <FaRecycle className="eco-icon" />
                                </div>
                                <h3>Think Plastic Recycling is Saving the Planet? Think Again.</h3>
                            </div>
                            <div className="eco-content">
                                <p>Here&rsquo;s the truth: most plastic doesn&rsquo;t get recycled&mdash;it ends up in landfills. Why? Because recycling plastic is no longer profitable, and many facilities simply can&rsquo;t afford to do it.</p>
                                <p className="eco-highlight">But here&rsquo;s the good news: aluminum is the real MVP. Of all the aluminum produced since 1888, more than 75% is still in use today. That&rsquo;s sustainability in action!</p>
                                <p>So next time you reach for a TitleOne bottled water, remember&mdash;you&rsquo;re holding one of the most recyclable materials on Earth. Cheers to that!</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {gameActive && !gameComplete && (
                    <div className="game-playing">
                        <div className="game-stats-top">
                            <div className="stat-card glass-card">
                                <span className="stat-label">Taps</span>
                                <span className="stat-value">{tapCount}</span>
                            </div>
                            <div className="stat-card glass-card">
                                <span className="stat-label">Time</span>
                                <span className="stat-value">{gameTime}s</span>
                            </div>
                        </div>
                        
                        <button
                            className="btn btn-quit-small"
                            onClick={(e) => {
                                e.stopPropagation();
                                resetGame();
                            }}
                            aria-label="Quit game"
                        >
                            <FaTimes /> Quit
                        </button>
                        <div 
                            ref={bottleRef}
                            className="bottle-game-area"
                            onClick={handleBottleTap}
                        >
                            <div className={`water-bottle-container ${bottleShake ? 'bottle-shake' : ''}`}>
                                <div className={`water-bottle ${getBottleFillClass()}`}>
                                    <div 
                                        className={`water-fill ${getWaterFillClass()}`}
                                        style={{ height: `${waterLevel}%` }}
                                    >
                                        <div className="water-wave"></div>
                                        {waterLevel > 10 && (
                                            <>
                                                <div className="bubble bubble-1"></div>
                                                <div className="bubble bubble-2"></div>
                                                <div className="bubble bubble-3"></div>
                                                <div className="bubble bubble-4"></div>
                                                <div className="bubble bubble-5"></div>
                                            </>
                                        )}
                                    </div>
                                    {splashParticles.map(particle => (
                                        <div
                                            key={particle.id}
                                            className="splash-particle"
                                            style={{
                                                left: particle.x,
                                                top: particle.y,
                                                '--tx': `${particle.tx}px`,
                                                '--ty': `${particle.ty}px`
                                            }}
                                        />
                                    ))}
                                    <div className="bottle-outline">
                                        <IoWaterOutline />
                                    </div>
                                    <div className={`fill-percentage ${waterLevel > 70 ? 'percentage-high' : ''}`}>
                                        {Math.round(waterLevel)}%
                                    </div>
                                    {showMilestone && (
                                        <div className="milestone-flash"></div>
                                    )}
                                </div>
                                {showMilestone && (
                                    <div className="milestone-text">
                                        <span className="milestone-icon">{milestoneIcon}</span>
                                        <span className="milestone-label">{milestoneText}</span>
                                    </div>
                                )}
                                <p className="tap-instruction">TAP ME!</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {gameComplete && (
                    <div className="game-complete-fullscreen">
                        <div className="glass-card results-card">
                            <div className="completion-animation">
                                {achievement.icon === 'rocket' && <FaRocket className="achievement-icon rocket" style={{ color: achievement.color }} />}
                                {achievement.icon === 'fire' && <FaFire className="achievement-icon fire" style={{ color: achievement.color }} />}
                                {achievement.icon === 'bolt' && <FaBolt className="achievement-icon bolt" style={{ color: achievement.color }} />}
                                {achievement.icon === 'wave' && <GiWaveCrest className="achievement-icon wave" style={{ color: achievement.color }} />}
                                {achievement.icon === 'dropletfill' && <BsDropletFill className="achievement-icon dropletfill" style={{ color: achievement.color }} />}
                                {achievement.icon === 'waterdrop' && <GiWaterDrop className="achievement-icon waterdrop" style={{ color: achievement.color }} />}
                            </div>
                            <h3 className="achievement-title">{achievement.title}!</h3>
                                    
                            {achievement.leaderboardRank && (
                                <div className="leaderboard-rank-announcement">
                                    <FaTrophy style={{ color: achievement.leaderboardRank <= 3 ? '#b3985d ' : '#000000' }} />
                                    <span>You ranked #{achievement.leaderboardRank} on the leaderboard!</span>
                                </div>
                            )}
                                    
                            <div className="final-stats">
                                <div className="final-stat glass-stat">
                                    <span className="final-stat-label">Your Time</span>
                                    <span className="final-stat-value">{gameTime}s</span>
                                </div>
                                <div className="final-stat glass-stat">
                                    <span className="final-stat-label">Total Taps</span>
                                    <span className="final-stat-value">{tapCount}</span>
                                </div>
                                {bestScore && gameTime.toString() === bestScore.toString() && (
                                    <div className="new-record">
                                        <FaTrophy className="record-icon" /> New Personal Best! <FaTrophy className="record-icon" />
                                    </div>
                                )}
                            </div>
                                    
                            {canDismissResults && (
                                <>
                                    <div className="social-share-section">
                                        <h4 className="share-title">
                                            <FaShare /> Thanks for playing!
                                        </h4>
                                        <p className="share-subtitle">Follow us for title tips, escrow insights, and a bit of fun!</p>
                                        <div className="social-buttons">
                                            <button 
                                                className="social-btn linkedin"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSocialClick('linkedin', true);
                                                }}
                                                aria-label="Share on LinkedIn"
                                            >
                                                <FaLinkedin />
                                            </button>
                                            <button 
                                                className="social-btn instagram"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSocialClick('instagram', true);
                                                }}
                                                aria-label="Share on Instagram"
                                            >
                                                <FaInstagram />
                                            </button>
                                            <button 
                                                className="social-btn facebook"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSocialClick('facebook', true);
                                                }}
                                                aria-label="Share on Facebook"
                                            >
                                                <FaFacebook />
                                            </button>
                                        </div>
                                    </div>
                                            
                                    <div className="game-actions">
                                        <button 
                                            className="btn btn-primary game-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                startGame();
                                            }}
                                        >
                                            Play Again
                                        </button>
                                        <button 
                                            className="btn btn-secondary game-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowLeaderboard(true);
                                            }}
                                        >
                                            <FaListOl /> View Leaderboard
                                        </button>
                                        <button 
                                            className="btn btn-secondary game-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                resetGame();
                                            }}
                                        >
                                            Back to Home
                                        </button>
                                    </div>
                                </>
                            )}
                                    
                            {!canDismissResults && (
                                <p className="results-wait-message">
                                    <GiWaterDrop className="wait-icon" /> Amazing work!
                                </p>
                            )}
                          
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
                                    
export default HydrationHero;
